import * as React from "react";
import "dayjs/locale/de";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers";
// @mui
import {
  Card,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  TextField,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  Paper,
  Typography,
  Box,
} from "@mui/material";
// components
import Iconify from "../../../components/iconify";
import Label from "../../../components/label";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import { useGetDocumentActionLogsQuery } from "../../../features/actionLog/actionLogApiSlice";
import { useGetTenantsQuery } from "../../../features/tenants/tenantsApiSlice";
import ActionLogHistoryDialog from "./ActionLogHistoryDialog";
import {
  ACTION_OPTIONS,
  RESULT_OPTIONS,
  resultColor,
  formatBytes,
} from "./fileActionLogUtils";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  "Zeit",
  "Benutzer",
  "Praxis",
  "Aktion",
  "Ergebnis",
  "Datei",
  "Größe",
  "Leer",
];

export default function FileActionLogOverview() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [action, setAction] = React.useState("");
  const [result, setResult] = React.useState("");
  const [tenant, setTenant] = React.useState("");
  const [from, setFrom] = React.useState(null);
  const [to, setTo] = React.useState(null);

  const { data: tenants = [] } = useGetTenantsQuery();

  // Debounce the free-text search so we don't refetch on every keystroke.
  const [searchInput, setSearchInput] = React.useState("");
  const [q, setQ] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = React.useMemo(() => {
    const p = { page: page + 1, limit: rowsPerPage };
    if (action) p.action = action;
    if (result) p.result = result;
    if (tenant) p.tenant = tenant;
    if (q) p.q = q;
    if (from && from.isValid()) p.from = from.toISOString();
    if (to && to.isValid()) p.to = to.toISOString();
    return p;
  }, [page, rowsPerPage, action, result, tenant, q, from, to]);

  const { data, isFetching } = useGetDocumentActionLogsQuery(params);
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const [historyTarget, setHistoryTarget] = React.useState(null);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const resetFilterPage = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  const isEmpty = !isFetching && rows.length === 0;

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Dokumenten-Protokoll
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Alle Dokument-Aktionen aller Praxen (neueste zuerst). Klick auf eine
        Zeile öffnet den Verlauf des Dokuments.
      </Typography>

      <Card>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ p: 2.5 }}
        >
          <OutlinedInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Benutzer oder Datei suchen…"
            size="small"
            sx={{ minWidth: 260 }}
            startAdornment={
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: "text.disabled", width: 20, height: 20 }}
                />
              </InputAdornment>
            }
          />

          <TextField
            select
            label="Aktion"
            size="small"
            value={action}
            onChange={(e) => resetFilterPage(setAction)(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Alle Aktionen</MenuItem>
            {ACTION_OPTIONS.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Ergebnis"
            size="small"
            value={result}
            onChange={(e) => resetFilterPage(setResult)(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Alle Ergebnisse</MenuItem>
            {RESULT_OPTIONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Praxis"
            size="small"
            value={tenant}
            onChange={(e) => resetFilterPage(setTenant)(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Alle Praxen</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.tenantName}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
            <DateTimePicker
              label="Von"
              value={from}
              onChange={resetFilterPage(setFrom)}
              slotProps={{
                textField: { size: "small", sx: { minWidth: 190 } },
                actionBar: { actions: ["clear", "accept"] },
              }}
            />
            <DateTimePicker
              label="Bis"
              value={to}
              onChange={resetFilterPage(setTo)}
              slotProps={{
                textField: { size: "small", sx: { minWidth: 190 } },
                actionBar: { actions: ["clear", "accept"] },
              }}
            />
          </LocalizationProvider>
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 960 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    hover
                    key={row._id}
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      row.fileId &&
                      setHistoryTarget({
                        fileId: row.fileId,
                        fileName: row.fileName,
                      })
                    }
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {new Date(row.createdAt).toLocaleString("de-DE")}
                    </TableCell>
                    <TableCell>{row.username || "—"}</TableCell>
                    <TableCell>{row.tenant?.tenantName || "—"}</TableCell>
                    <TableCell>{row.action}</TableCell>
                    <TableCell>
                      <Label color={resultColor(row.result)}>
                        {row.result}
                      </Label>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.fileName || ""}
                    >
                      {row.fileName || "—"}
                    </TableCell>
                    <TableCell>{formatBytes(row.sizeBytes)}</TableCell>
                    <TableCell>
                      {row.isEmpty === true ? (
                        <Label color="error">leer</Label>
                      ) : row.isEmpty === false ? (
                        <Label color="default">nein</Label>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {isEmpty && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 6 }}>
                      <Paper sx={{ textAlign: "center", boxShadow: "none" }}>
                        <Typography variant="h6" paragraph>
                          Keine Einträge
                        </Typography>
                        <Typography variant="body2">
                          Für die aktuellen Filter wurden keine
                          Protokolleinträge gefunden.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <Box sx={{ position: "relative" }}>
          {isFetching && (
            <Typography
              variant="caption"
              sx={{ position: "absolute", left: 16, top: 20, color: "text.disabled" }}
            >
              Aktualisiere…
            </Typography>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Zeilen pro Seite:"
          />
        </Box>
      </Card>

      <ActionLogHistoryDialog
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        fileId={historyTarget?.fileId}
        fileName={historyTarget?.fileName}
      />
    </>
  );
}
